import { Spinner, Text, Tooltip } from "@shopify/polaris"
import classNames from "classnames"
import { ReactNode, useState } from "react"

import CheckCircle from "@/assets/svg/CheckCircle"
import HoverCircle from "@/assets/svg/HoverCircle"
import HoverSincere from "@/assets/svg/HoverSincere"

import styles from "./QuickSetup.module.scss"

interface QuickSetupItemProps {
  title: string
  completed: boolean
  expanded: boolean
  loading?: boolean
  titleSuffix?: ReactNode
  image?: ReactNode
  children?: ReactNode
  onExpand: () => void
  onToggleComplete: (next: boolean) => void
}

export default function QuickSetupItem({
  title,
  completed,
  expanded,
  loading = false,
  titleSuffix,
  image,
  children,
  onExpand,
  onToggleComplete,
}: QuickSetupItemProps) {
  const [circleHovered, setCircleHovered] = useState(false)

  const renderCircle = () => {
    if (loading) {
      return (
        <div className="w-[20px] h-[20px]">
          <Spinner accessibilityLabel={t("Loading")} size="small" />
        </div>
      )
    }
    if (completed) {
      return (
        <Tooltip
          content={t("MarkAsNotDone")}
          preferredPosition="above"
          dismissOnMouseOut
        >
          <div className="flex">
            <CheckCircle />
          </div>
        </Tooltip>
      )
    }
    return (
      <Tooltip
        content={t("MarkAsDone")}
        preferredPosition="above"
        dismissOnMouseOut
      >
        <div
          className="flex"
          onMouseEnter={() => setCircleHovered(true)}
          onMouseLeave={() => setCircleHovered(false)}
        >
          {circleHovered ? <HoverSincere /> : <HoverCircle />}
        </div>
      </Tooltip>
    )
  }

  return (
    <div
      className={classNames(styles.item, {
        [styles.itemExpanded]: expanded,
        "cursor-pointer": !expanded,
      })}
      onClick={() => {
        if (!expanded) onExpand()
      }}
    >
      <div
        className="flex-shrink-0 mt-[2px] cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          onToggleComplete(!completed)
        }}
      >
        {renderCircle()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <Text
            variant={expanded ? "headingSm" : "bodyMd"}
            as="span"
            tone={completed && !expanded ? "subdued" : undefined}
          >
            {title}
          </Text>
          {expanded && titleSuffix}
        </div>
        {expanded && <div className="mt-2">{children}</div>}
      </div>
      {expanded && image && <div className={styles.itemImage}>{image}</div>}
    </div>
  )
}
